<template>
  <div class="v2-address-section v2-card v2-p-4">
    <div class="v2-address-section__grid">
      <!-- Left: Address + Notes + Delivery -->
      <div class="v2-address-section__content">
        <!-- Shipping Address -->
        <div class="v2-address-section__label">
          Shipping address
        </div>
        <div
          v-if="selectedShippingAddress"
          class="v2-address-section__address-grid"
        >
          <div class="v2-address-section__address-col">
            <div>{{ selectedShippingAddress.contactName }},</div>
            <div>{{ selectedShippingAddress.addressLine1 }}, {{ selectedShippingAddress.addressLine2 }},</div>
            <div>
              {{ selectedShippingAddress.city }}, {{ selectedShippingAddress.country }} - <span>{{ selectedShippingAddress.zipcode }}</span>
            </div>
          </div>
          <div class="v2-address-section__address-col">
            <div>Logistic Contact: {{ selectedShippingAddress.contactNumber }}</div>
            <div class="v2-address-section__email">
              {{ selectedShippingAddress.contactEmail }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="v2-address-section__address-value"
        >
          -
        </div>

        <!-- Notes and Delivery Window -->
        <div class="v2-address-section__fields-grid">
          <!-- Notes -->
          <div>
            <div class="v2-address-section__field-header">
              <span class="v2-address-section__label">Notes</span>
              <v2-button
                v-if="isEditNotes"
                size="sm"
                :disabled="isSavingNotes"
                @click="onUpdateNotes"
              >
                {{ isSavingNotes ? 'Saving' : 'Save' }}
              </v2-button>
              <button
                v-if="!isEditNotes"
                class="v2-address-section__edit-btn"
                @click="handleEditNote"
              >
                <!-- Edit icon -->
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
            <div class="v2-address-section__field-body">
              <textarea
                v-if="isEditNotes"
                v-model="cart.notes"
                class="v2-address-section__textarea"
                placeholder="Enter Notes"
                @input="debouncedTrackNoteInput(cart.notes, cart)"
              />
              <span
                v-else
                class="v2-address-section__address-value"
              >{{ cart.notes || '-' }}</span>
            </div>
          </div>

          <!-- Requested Delivery Window -->
          <div>
            <div class="v2-address-section__field-header">
              <span class="v2-address-section__field-heading">Requested Delivery Window:</span>
              <template v-if="!isEditDeliveryWindow">
                <span class="v2-address-section__field-value">{{ expectedDeliveryDate || '-' }}</span>
                <button
                  class="v2-address-section__edit-btn"
                  @click="handleEditDeliveryWindow"
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </template>
            </div>
            <div
              v-if="isEditDeliveryWindow"
              class="v2-address-section__delivery-edit"
            >
              <div class="v2-address-section__delivery-input-wrap">
                <flat-pickr
                  ref="deliveryWindowDate"
                  v-model="expectedDeliveryDate"
                  class="v2-address-section__date-input"
                  :config="deliveryDateConfig"
                  :placeholder="KP_MONTH_FORMAT.PLACEHOLDER"
                />
                <button
                  class="v2-address-section__calendar-btn"
                  @click="toggleDeliveryWindowPicker"
                >
                  <!-- Calendar icon -->
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      x="3"
                      y="4"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"
                    />
                    <line
                      x1="16"
                      y1="2"
                      x2="16"
                      y2="6"
                    />
                    <line
                      x1="8"
                      y1="2"
                      x2="8"
                      y2="6"
                    />
                    <line
                      x1="3"
                      y1="10"
                      x2="21"
                      y2="10"
                    />
                  </svg>
                </button>
              </div>
              <v2-button
                size="sm"
                :disabled="isSavingDeliveryWindow"
                @click="onUpdateDeliveryWindow"
              >
                {{ isSavingDeliveryWindow ? 'Saving' : 'Save' }}
              </v2-button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Select Address Button -->
      <div class="v2-address-section__select-btn-wrap">
        <span
          v-if="!selectedShippingAddress"
          class="v2-address-section__tooltip-wrap"
          title="No Addresses Found"
        >
          <v2-button
            variant="outline"
            disabled
          >
            Select Another Address
          </v2-button>
        </span>
        <v2-button
          v-else
          variant="outline"
          @click="onClickSelectAddress"
        >
          Select Another Address
        </v2-button>
      </div>
    </div>

    <!-- Select Address Modal -->
    <v2-modal
      :show="showAddressModal"
      title="Select Address"
      size="lg"
      @close="showAddressModal = false"
    >
      <address-list
        class="v2-mb-2"
        :address-type="ADDRESSES_TYPE.SHIPPING"
        :is-popup="true"
        :is-multiple-cart="true"
        :address="shippingAddress"
        :commons="countryData"
        :set-as-primary-address="setAsPrimaryAddress"
      />
    </v2-modal>
  </div>
</template>

<script>
import V2Button from '@/components/v2/V2Button.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import { UPDATE_CART_NOTES, UPDATE_CART_EXPECTED_DELIVERY_DATE } from '@/store/modules/shop.module'
import { parseDeliveryDate } from '@/composables/useCartTotal'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { mapState } from 'vuex'
import analytics from '@/@core/utils/analytics'
import constants, { VUE_FLATPICKR_MONTH_CONFIG, MONTH_START_DATE } from '@/constants'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { GET_COUNTRIES } from '@/store/modules/common.module'
import { debounce } from 'lodash'
import flatPickr from 'vue-flatpickr-component'
import AddressList from '../../apps/account-setting/multiple-address/AddressList.vue'
import AddressMixin from '../../apps/account-setting/multiple-address/AddressMixin.vue'

const { ADDRESSES_TYPE, KP_MONTH_FORMAT } = c

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES

export default {
  name: 'V2AddressSection',
  components: {
    V2Button,
    V2Modal,
    AddressList,
    flatPickr,
  },
  mixins: [AddressMixin],
  props: {
    cart: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isEditNotes: false,
      isSavingNotes: false,
      isEditDeliveryWindow: false,
      isSavingDeliveryWindow: false,
      expectedDeliveryDate: '',
      selectedShippingAddress: null,
      ADDRESSES_TYPE,
      addresses: [],
      countryData: null,
      KP_MONTH_FORMAT,
      showAddressModal: false,
    }
  },
  computed: {
    ...mapState({
      commons: state => state.common.commonData,
    }),
    deliveryDateConfig() {
      return VUE_FLATPICKR_MONTH_CONFIG
    },
  },
  created() {
    this.loadCountries()
    this.userId = this.cart?.userId
    this.selectAddressModalId = `select_address_modal${this.cart._id}`
    this.addresses = this.cart?.addresses || []
    if (this.addresses) {
      this.loadShippingAddress()
    }
    // Initialize expected delivery date from cart
    if (this.cart?.expectedDeliveryDate) {
      this.expectedDeliveryDate = parseDeliveryDate(this.cart.expectedDeliveryDate)
    }
  },
  methods: {
    loadCountries() {
      this.$store.dispatch(GET_COUNTRIES).then(res => {
        this.countryData = res?.data?.data
      }).catch(err => {
        apiToastError(err)
      })
    },
    onClickSelectAddress() {
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_SELECT_ANOTHER_ADDRESS_BUTTON, { shippingAddress: this.shippingAddress })
      this.showAddressModal = true
    },
    handleEditNote() {
      this.isEditNotes = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_NOTES_ICON, {
        cart: this.cart,
      })
    },
    debouncedTrackNoteInput: debounce((textInput, cart) => {
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_ENTERS_TEXT_IN_NOTES, {
        textInput,
        cart,
      })
    }, 500),
    async onUpdateNotes() {
      this.isSavingNotes = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_SAVE_NOTES_BTN, {
        cart: this.cart,
        textInput: this.cart.notes,
      })
      this.$store.dispatch(UPDATE_CART_NOTES, { payload: { cartId: this.cart._id, notes: this.cart.notes } }).then(res => {
        this.isSavingNotes = false
        this.isEditNotes = false
        apiToastSuccess(res.data.message)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_NOTE_SUCCESS, {
          cart: this.cart,
          response: res,
        })
      }).catch(err => {
        this.isSavingNotes = false
        apiToastError(err)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_NOTE_FAILED, {
          cart: this.cart,
          error: err,
        })
      })
    },
    loadShippingAddress() {
      this.selectedShippingAddress = this.addresses.find(address => address?.addressType === ADDRESSES_TYPE.SHIPPING && address?.isPrimary)
      this.getBillingAndShippingAddress(this.addresses)
    },
    handleEditDeliveryWindow() {
      this.isEditDeliveryWindow = true
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_DELIVERY_WINDOW_ICON, {
        cart: this.cart,
      })
      this.$nextTick(() => {
        this.toggleDeliveryWindowPicker()
      })
    },
    toggleDeliveryWindowPicker() {
      if (this.$refs.deliveryWindowDate?.$el?._flatpickr) {
        this.$refs.deliveryWindowDate.$el._flatpickr.toggle()
      }
    },
    isValidDeliveryDate() {
      if (!this.expectedDeliveryDate) return false
      const [month, year] = this.expectedDeliveryDate.split('/')
      const selectedDate = new Date(year, month - 1, 1)
      const now = new Date()
      const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
      return selectedDate >= currentMonth
    },
    async onUpdateDeliveryWindow() {
      if (!this.isValidDeliveryDate()) {
        apiToastError('Please select current or future month')
        return
      }
      this.isSavingDeliveryWindow = true
      const formattedDate = `${MONTH_START_DATE}${this.expectedDeliveryDate}`
      analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_CLICKS_SAVE_DELIVERY_WINDOW_BTN, {
        cart: this.cart,
        expectedDeliveryDate: formattedDate,
      })
      this.$store.dispatch(UPDATE_CART_EXPECTED_DELIVERY_DATE, {
        payload: {
          cartId: this.cart._id,
          expectedDeliveryDate: formattedDate,
        },
      }).then(res => {
        this.isSavingDeliveryWindow = false
        this.isEditDeliveryWindow = false
        this.cart.expectedDeliveryDate = formattedDate
        apiToastSuccess(res.data.message)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_DELIVERY_WINDOW_SUCCESS, {
          cart: this.cart,
          response: res,
        })
      }).catch(err => {
        this.isSavingDeliveryWindow = false
        apiToastError(err)
        analytics.track(constants.TRACKS.ACTIONS.ORDER_DETAILS_PAGE.MULTIPLE_STORES.RETAILER_SAVE_DELIVERY_WINDOW_FAILED, {
          cart: this.cart,
          error: err,
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-address-section {
  background: var(--v2-muted);
  border-radius: var(--v2-radius-lg);
  margin-bottom: var(--v2-space-2);

  &__grid {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--v2-space-4);
  }

  &__content {
    min-width: 0;
  }

  &__label {
    font-size: 1rem;
    font-weight: 700;
    color: var(--v2-foreground);
  }

  &__address-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-2);
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    line-height: 1.6;
  }

  &__address-col {
    min-width: 0;
  }

  &__email {
    text-transform: lowercase;
  }

  &__address-value {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__fields-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-4);
  }

  &__field-header {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
  }

  &__field-heading {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
  }

  &__field-value {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-muted-foreground);
  }

  &__field-body {
    margin-top: var(--v2-space-2);
  }

  &__edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: var(--v2-background);
      color: var(--v2-primary);
    }
  }

  // Glass textarea — matches V2Input recipe.
  &__textarea {
    width: 100%;
    min-height: 80px;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    resize: vertical;
    outline: none;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      border-color: var(--v2-primary);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__delivery-edit {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-2);
  }

  &__delivery-input-wrap {
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
  }

  // Glass date input — matches V2DatePicker recipe.
  &__date-input {
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    outline: none;
    width: 120px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      border-color: var(--v2-primary);
    }

    &::placeholder {
      color: var(--v2-muted-foreground);
    }
  }

  &__calendar-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__select-btn-wrap {
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
  }

  &__tooltip-wrap {
    display: inline-block;
    cursor: not-allowed;
  }
}

.v2-mb-2 {
  margin-bottom: var(--v2-space-2);
}
</style>
